import React from 'react';
import cx from 'classnames';

import {Autocomplete, AutocompleteItem, Select, SelectSection, SelectItem, SelectProps, Skeleton} from "@heroui/react";

import FlexRow from './FlexRow';

export type DropdownProps = SelectProps & {
  label?:			string;
  flex?:			number;
  isLoaded?:		boolean;
  minWidth?:		any;
  maxSelected?:		number;
  width?:			any;
  height?:			any;
  options:			FieldInfo[];
  value?:			React.Key | React.Key[];
  setValue?:		(val: any) => any;
  row?:				boolean;
};

export interface FieldInfo {
	value:			any;
	desc:			string;
	data?:			any;
	// Hover text for one option. Used with `disabledKeys` to say WHY an option
	// cannot be picked (e.g. "MW light user on this project") instead of
	// greying it out with no explanation.
	tooltip?:		string;
}

const dropdownDefaultH = "h-auto"; //36;

const styles = {
  flex: [
    'flex-0',
    'flex-1',
    'flex-2',
    'flex-3',
  ],
};

const pick = (...props: any[]) => (o: any) => props.reduce((a, e) => ({ ...a, [e]: o[e] }), {});

const Dropdown = (dprops: DropdownProps) => {
  const {className, children, disabledKeys, isDisabled, label, height, flex, isLoaded, maxSelected, options, selectionMode, value, width, minWidth, row, setValue, onChange, variant, ...props} = dprops;

  const autoProps = pick('isInvalid', 'errorMessage', 'isDisabled')(dprops);
  
  const mw =	minWidth === undefined
	  			?	"min-w-[250px]"
	  			:	minWidth;
  
  const ms =	maxSelected === undefined
  				?	1000
  				:	maxSelected;
  
  const h = height
  			?	height
  			:	dropdownDefaultH;
  
  const f = flex === undefined
  			?	1
  			:	flex;
  
  const v = variant === undefined
  			?	"bordered"
  			:	variant;
  
  const sm = selectionMode=== undefined
  			 ?	"single"
  			 :	selectionMode;
  
  const loaded = isLoaded === undefined
  				 ?	true
  				 :	isLoaded;
  
  const sendVal =	sm === "multiple"
  					?	value
  					:	(	Array.isArray(value) &&
  							value.length
  							?	value[0]
  							:	""
  						);
  
  const classes = cx(
    'flex',
    styles.flex[f],
    width === undefined ? 'w-full' : width,
    h,
    className,
  );
  
  /*const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (setValue)
      setValue(
      	event.target && event.target.value
        ?	event.target.value
        :	''
      );
  };*/
  
  const handleSelectionChange = (vals: "all" | Set<React.Key>) => {
    if (	vals === "all" ||
        	!setValue
       )
      return
      
    setValue(
        selectionMode == "multiple"
        ?	[...vals]
        :	(	vals.size
        		?	(	[...vals][0]
        				?	[...vals][0]
        				:	""
        			)
        		:	""
        	)
    );
  };
  
  const handleAutoSelectionChange = (val: React.Key | null) => {
    if (!setValue)
      return;
    
    setValue(
    	val
    	?	(	Array.isArray(value)
    			?	[val]
    			:	val
    		)
    	:	(	Array.isArray(value)
    			?	[]
    			:	''
    		)
    );
  };
  
  const selKeys: any[] =	value
  							?	(	Array.isArray(value)
  									?	value
  									:	[value]
  								)
  							:	[];
  
  const out = 
    sm == "multiple"
    ?  
  <Select className={classes}
          classNames={{
            trigger: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] max-h-[52px]",
            label: "!text-black r10",
            listboxWrapper: "px-0",
            listbox: "px-0",
            popoverContent: "px-0",
            value: "!text-black r14",
          }}
          selectedKeys={selKeys}
          variant={v}
		  label={label}
          selectionMode={sm}
          isDisabled={isDisabled}
          // Passed through rather than swallowed: callers use it to stop a
          // name being picked in two mutually exclusive lists at once.
          disabledKeys={disabledKeys}
          disallowEmptySelection={true}
          //onChange={handleChange}
          onSelectionChange={handleSelectionChange}
          value={value}
          aria-label="select"
          {...props}>
    {options?.map((opt: FieldInfo, i: number) =>
      <SelectItem key={opt.value}
                  classNames={{
                    base: "px-[8px] rounded-none data-[hover=true]:bg-[--primetime-30] data-[selectable=true]:focus:bg-[--primetime-30]",
                  }}
                  textValue={opt.desc}
                  aria-label={opt.desc}>
        {opt.tooltip
          ? <span title={opt.tooltip}>{opt.desc}</span>
          : opt.desc
        }
      </SelectItem>
    )}
  </Select>
    :
  <Autocomplete className={classes}
          classNames={{
            //trigger: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] max-h-[52px]",
            //label: "!text-black r10",
            listboxWrapper: "px-0",
            listbox: "px-0",
            popoverContent: "px-0",
            //value: "!text-black r14",
          }}
          defaultItems={options}
          selectedKey={selKeys.length ? selKeys[0] : ''}
          variant={v}
		  label={label}
          onSelectionChange={handleAutoSelectionChange}
          aria-label="select"
          value={sendVal}
          {...autoProps}>
    {(item: FieldInfo) =>
      <AutocompleteItem key={item.value}>
        {item.desc}
      </AutocompleteItem>
    }
  </Autocomplete>;
  
  if (!loaded) {
    return (
      <Skeleton isLoaded={loaded}
                className="w-full">
        { out }
      </Skeleton>
    );
  }
  
  return out;
}

export default Dropdown;


